import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { serverTimestamp } from 'firebase/firestore';
import type { Client } from '@altyor-kronos/shared-types';
import { useAuth } from '../../core/auth/useAuth';
import { useCollectionSnapshot, orderByName } from '../../core/data/useCollectionSnapshot';
import { createClient, updateClient, archiveClient } from '../../core/data/adminMutations';
import ClientWizard, { type ClientFormData } from './ClientWizard';
import { colors } from '../../core/theme/theme';

function ClientAvatar({ src, name }: { src?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
  return (
    <Avatar
      src={failed ? undefined : (src ?? undefined)}
      variant="square"
      sx={{ width: 40, height: 40, fontSize: 16, fontWeight: 700, bgcolor: colors.lightGray }}
      slotProps={{ img: { onError: () => setFailed(true) } }}
    >
      {initials}
    </Avatar>
  );
}

export default function ClientsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;
  const { rows, loading, error } = useCollectionSnapshot<Client>('clients', orderByName());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [wizardEditing, setWizardEditing] = useState<{ id: string; data: Partial<Client> } | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; name: string; isArchived: boolean } | null>(null);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open wizard when navigated with ?new=1 (e.g. from Hattori palette)
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setDialogOpen(true);
      setWizardEditing(null);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [showArchived, setShowArchived] = useState(false);

  const allForUniqueness = useMemo(
    () => rows.map(({ id }) => ({ id })),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const visible = rows.filter(({ data }) => showArchived || !data.archivedAt);
    if (!q) return visible;
    return visible.filter(({ data }) =>
      data.name.toLowerCase().includes(q) ||
      (data.industry ?? '').toLowerCase().includes(q) ||
      (data.country ?? '').toLowerCase().includes(q) ||
      (data.tags ?? []).some((tag) => tag.toLowerCase().includes(q)),
    );
  }, [rows, search, showArchived]);

  const onCreate = () => {
    setWizardEditing(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (values: ClientFormData) => {
    const foundedYearNum = values.foundedYear ? parseInt(values.foundedYear, 10) : undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: Record<string, any> = {
      name: values.name,
      description: values.description || null,
      logoURL: values.logoURL || null,
      website: values.website || null,
      linkedinURL: values.linkedinURL || null,
      industry: values.industry || null,
      companyType: values.companyType || null,
      employeeRange: values.employeeRange || null,
      country: values.country || null,
      city: values.city || null,
      foundedYear: foundedYearNum !== undefined && !isNaN(foundedYearNum) ? foundedYearNum : null,
      parentCompany: values.parentCompany || null,
      tags: values.tags.length > 0 ? values.tags : null,
      updatedAt: serverTimestamp(),
    };

    if (wizardEditing) {
      await updateClient(wizardEditing.id, payload);
    } else {
      await createClient(payload);
    }
  };

  const handleArchiveToggle = async () => {
    if (!archiveTarget) return;
    await archiveClient(archiveTarget.id, archiveTarget.isArchived);
    setArchiveTarget(null);
  };

  return (
    <Box>
      {/* Header: search + toggle archived + new client */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 260 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 auto', sm: '0 0 auto' } } }}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? t('admin.clients.hideArchived') : t('admin.clients.showArchived')}
          </Button>
          {isAdmin && (
            <Button variant="contained" color="primary" onClick={onCreate}>
              {t('admin.clients.new')}
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

      {loading && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          {t('common.loading')}
        </Typography>
      )}

      {!loading && filtered.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          {t('common.noResults')}
        </Typography>
      )}

      {/* Client cards grid */}
      <Grid container spacing={2}>
        {filtered.map(({ id, data }) => {
          const isArchived = Boolean(data.archivedAt);
          return (
            <Grid key={id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'border-color 150ms',
                  opacity: isArchived ? 0.55 : 1,
                  '&:hover': { borderColor: colors.yellow },
                }}
              >
                <CardActionArea onClick={() => navigate(`/customers/${id}`)} sx={{ flex: 1, p: 0 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, '&:last-child': { pb: 2 } }}>
                    {/* Logo + Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <ClientAvatar src={data.logoURL} name={data.name} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                          {data.name}
                        </Typography>
                        {data.industry && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {data.industry}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Chips row: companyType + country + archived */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {isArchived && (
                        <Chip
                          size="small"
                          icon={<ArchiveOutlinedIcon />}
                          label={t('admin.clients.archived')}
                          sx={{ borderRadius: '6px', color: colors.textMuted }}
                        />
                      )}
                      {data.companyType && (
                        <Chip
                          label={t(`admin.clients.companyTypes.${data.companyType}`)}
                          size="small"
                          sx={{ fontSize: 11, height: 22 }}
                        />
                      )}
                      {data.country && (
                        <Chip label={data.country} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                      )}
                      {data.employeeRange && (
                        <Chip label={data.employeeRange} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                      )}
                    </Box>

                    {/* Description snippet */}
                    {data.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4,
                        }}
                      >
                        {data.description}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>

                {/* Footer actions */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  {data.website && (
                    <Tooltip title={data.website}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); window.open(data.website!, '_blank'); }}
                      >
                        <LanguageIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isAdmin && (
                    <Tooltip title={isArchived ? t('admin.clients.unarchive') : t('admin.clients.archive')}>
                      <IconButton
                        size="small"
                        aria-label={isArchived ? t('admin.clients.unarchive') : t('admin.clients.archive')}
                        onClick={(e) => { e.stopPropagation(); setArchiveTarget({ id, name: data.name, isArchived }); }}
                      >
                        {isArchived ? <UnarchiveOutlinedIcon fontSize="small" /> : <ArchiveOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <ClientWizard
        open={dialogOpen}
        editing={wizardEditing}
        allClients={allForUniqueness}
        onSubmit={handleSubmit}
        onClose={() => setDialogOpen(false)}
      />

      {/* Archive / unarchive confirmation dialog */}
      <Dialog open={Boolean(archiveTarget)} onClose={() => setArchiveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {archiveTarget?.isArchived ? t('admin.clients.unarchiveDialog.title') : t('admin.clients.archiveDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {archiveTarget?.isArchived
              ? t('admin.clients.unarchiveDialog.body', { name: archiveTarget?.name ?? '' })
              : t('admin.clients.archiveDialog.body', { name: archiveTarget?.name ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)} variant="outlined" color="secondary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleArchiveToggle} variant="contained" color="secondary">
            {archiveTarget?.isArchived ? t('admin.clients.unarchiveDialog.confirm') : t('admin.clients.archiveDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
